/* eslint-disable max-lines */
import {
  BottomSheetWithIntent,
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { TimePicker } from "@src/appV2/redesign/components/TimePicker";
import { isAfter, isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import {
  type ClockOutErrorType,
  type ClockOutGeofenceValidationType,
  validateClockOutTimestamp,
} from "../../MissedPunches/utils/validateClockOutTimestamp";
import { isBoundaryError } from "../analytics/isBoundaryError";
import { getBottomSheetIntent } from "./utils/getBottomSheetIntent";
import { getDayIndicator } from "./utils/getDayIndicator";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockOutTimeFormValues {
  clockOutTime?: Date;
}

interface EditClockOutTimeBottomSheetProps {
  modalState: UseModalState;
  clockOutTime?: Date;
  workplaceTimezone: string;
  clockOutButtonTappedAt?: Date;
  breakEndTime?: Date;
  clockInTime?: Date;
  shiftStartAt: Date;
  shiftEndAt: Date;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
  isMissedPunchRequestEnabled: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockOut?: Date;
  shiftId?: string;
  workplaceId?: string;
  onConfirm: (clockOutTime: Date) => void;
  onCancel?: () => void;
}

export function EditClockOutTimeBottomSheet(props: EditClockOutTimeBottomSheetProps) {
  const {
    modalState,
    clockOutTime,
    workplaceTimezone,
    clockOutButtonTappedAt,
    breakEndTime,
    clockInTime,
    shiftStartAt,
    shiftEndAt,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
    isMissedPunchRequestEnabled,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockOut,
    shiftId,
    workplaceId,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);

  const { setValue, watch, reset } = useForm<EditClockOutTimeFormValues>({
    defaultValues: {
      clockOutTime: isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined,
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined;
      reset({
        clockOutTime: resetValue,
      });
      setSaveAttempted(false);
    }
  }, [modalState.modalIsOpen, clockOutTime, reset, workplaceTimezone]);

  const currentLocalClockOutTime = watch("clockOutTime");

  // Convert back to UTC for validation and submission
  const currentClockOutTime = isDefined(currentLocalClockOutTime)
    ? getUtcTimeFromPicker(currentLocalClockOutTime, workplaceTimezone)
    : undefined;

  const getValidationMessages = (
    selectedTime?: Date
  ): {
    errorMessage?: string;
    warningMessage?: string;
    infoMessage?: string;
    errorType?: ClockOutErrorType;
    geofenceValidationType?: ClockOutGeofenceValidationType;
  } => {
    if (!isDefined(selectedTime)) {
      return {};
    }

    // Use centralized validation when feature flag is enabled
    if (isMissedPunchRequestEnabled && isDefined(clockInTime) && isDefined(shiftEndAt)) {
      const validationResult = validateClockOutTimestamp({
        selectedTime,
        clockInTime,
        geofenceDepartureTime,
        latestPlausibleDepartureTime,
        shiftEndAt,
        workerClockOut,
        isGeofenceFacility,
        isNfcOrMeshFacility,
        isDirectIntegrationFacility,
        isEnforceGeofenceTimestampsEnabled,
        verifiedClockOut,
        workplaceTimezone,
      });

      // Return error message if validation failed
      if (!validationResult.isValid) {
        return {
          errorMessage: validationResult.message,
          errorType: validationResult.errorType,
          geofenceValidationType: validationResult.geofenceValidationType,
        };
      }

      // For the current phase of the missed punch rollout, we deliberately
      // suppress the non-blocking "You are recording a missed punch" banner.
      // The underlying validation still returns a MISSED_PUNCH_WARNING flag,
      // but we do not surface a message in this flow.

      //  if (validationResult.warningType === "MISSED_PUNCH_WARNING") {
      //   return { infoMessage: validationResult.message };
      // }

      return {};
    }

    // Legacy validation (when feature flag is off)
    // 1. If HCP attempts to edit clock out to after they tapped the clock out button
    if (isDefined(clockOutButtonTappedAt) && isAfter(selectedTime, clockOutButtonTappedAt)) {
      return {
        errorMessage: `Please select a clock out time at or before ${formatTimeWithTimezone(
          clockOutButtonTappedAt,
          workplaceTimezone,
          false
        )} — the time you tapped 'Clock Out'`,
      };
    }

    // 2. Clock out time is before break end
    if (isDefined(breakEndTime) && isBefore(selectedTime, breakEndTime)) {
      return { errorMessage: "You cannot clock out before your break ends." };
    }

    // 3. Clock out time is before or equal to clock in time
    if (isDefined(clockInTime) && !isAfter(selectedTime, clockInTime)) {
      return { errorMessage: "You cannot clock out before or at your clock in time." };
    }

    return {};
  };

  const { errorMessage, warningMessage, infoMessage, errorType, geofenceValidationType } =
    getValidationMessages(currentClockOutTime);

  const handleConfirm = () => {
    setSaveAttempted(true);

    // Track boundary violations when user attempts to save with an error
    if (isBoundaryError(errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "clock-out",
        errorType,
        shiftId,
        workplaceId,
        selectedTime: currentClockOutTime?.toISOString(),
        geofenceDepartureTime: geofenceDepartureTime?.toISOString(),
        shiftEndAt: shiftEndAt?.toISOString(),
        verifiedClockOut: verifiedClockOut?.toISOString(),
        latestPlausibleDepartureTime: latestPlausibleDepartureTime?.toISOString(),
        geofenceValidationType,
      });
    }

    if (isDefined(currentClockOutTime) && !errorMessage) {
      onConfirm(currentClockOutTime);
      modalState.closeModal();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      // Infer the correct date based on the shift window
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
      });

      // When success is false, time is outside valid window - still set it but validation will catch it
      const timeToSet = inferResult.success ? inferResult.date : newTime;
      setValue("clockOutTime", timeToSet, { shouldDirty: true });

      setSaveAttempted(false);
    }
  };

  const isValid = isDefined(currentClockOutTime);
  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentClockOutTime,
    referenceTimestamp: shiftEndAt,
    workplaceTimezone,
  });

  const { intent, intentContent, intentIcon } = getBottomSheetIntent({
    saveAttempted,
    errorMessage,
    warningMessage,
    infoMessage,
  });

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={intent}
      intentContent={intentContent}
      intentIcon={intentIcon}
      footer={
        <DialogFooter orientation="vertical" onClose={handleCancel}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Save
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title semibold variant="h4" component="h2">
            Edit time
          </Title>

          <Stack spacing={4}>
            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="clock" />
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexGrow: 1 }}>
                <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
                  Clock out
                </Text>
                {dayIndicator && (
                  <Text sx={{ color: (theme) => theme.palette.text.disabled }}>{dayIndicator}</Text>
                )}
              </Stack>
              <Box textAlign="right">
                <Button aria-label="Select clock out time" variant="outlined" size="small">
                  {isDefined(currentClockOutTime)
                    ? formatTimeWithTimezone(currentClockOutTime, workplaceTimezone, false)
                    : "--:-- --"}
                </Button>
              </Box>
            </Stack>

            <Divider />
            <TimePicker
              ampm
              skipDisabled
              preventParentTouchStart
              value={currentLocalClockOutTime}
              timeSteps={{ minutes: 1 }}
              containerSx={{
                // make sure the time picker takes as little horizontal space as possible
                // this preserves more area on the left/right for the bottom sheet to capture touch events
                // for swipe-down closing (dismiss)
                alignSelf: "center",
              }}
              sx={{
                justifyContent: "center",
                "& .MuiList-root": {
                  scrollbarWidth: "none",
                },
              }}
              onChange={handleTimeChange}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
/* eslint-enable max-lines */
